import { Box, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import React, { memo, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import BaseTableHeadReport from './BaseTableHeadReport';
import BasePagination from '~/components/jobs/base-pagination/BasePagination';
import styles from './BaseTableReport.module.scss';
import classNamesBind from 'classnames/bind';
import { SortDirection } from '~/utils/types/common';
import { HeadCelType } from '~/utils/types/common';
import { PAGE_INDEX } from '~/utils/constants/common';
import { useTranslation } from 'react-i18next';

const cx = classNamesBind.bind(styles);

function shouldIgnoreDragStart(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'input, textarea, button, a, label, svg, [role="button"], [role="checkbox"], .MuiCheckbox-root, .MuiIconButton-root, .MuiInputBase-root, .MuiOutlinedInput-root'
    )
  );
}

function useDragScrollHandlers(scrollEl: HTMLDivElement | null) {
  const dragRef = React.useRef<{ startX: number; scrollLeft: number } | null>(null);

  useEffect(() => {
    if (!scrollEl) return;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (shouldIgnoreDragStart(e.target)) return;
      dragRef.current = { startX: e.clientX, scrollLeft: scrollEl.scrollLeft };
      scrollEl.style.cursor = 'grabbing';
      scrollEl.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      e.preventDefault();
      const dx = e.clientX - d.startX;
      scrollEl.scrollLeft = d.scrollLeft - dx;
    };

    const endDrag = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      scrollEl.style.cursor = '';
      scrollEl.style.userSelect = '';
    };

    scrollEl.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      scrollEl.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [scrollEl]);
}

const ReportTableScrollContainer = memo(function ReportTableScrollContainer({
  children,
  tableSx,
}: {
  children: ReactNode;
  tableSx?: object;
}) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useDragScrollHandlers(el);

  return (
    <TableContainer
      component='div'
      ref={setEl}
      className={`rounded-t-xl border-b-0 bg-white ${cx('report-table-scroll')}`}
    >
      <Table stickyHeader sx={tableSx}>
        {children}
      </Table>
    </TableContainer>
  );
});

type BaseTableReportProps<T extends { id?: string }> = {
  data: Array<T>;
  renderCell: (item: T, index: number) => ReactElement;
  headCells: Array<HeadCelType>;
  /** Bật vùng cuộn + kéo ngang như bảng báo cáo tổng hợp; thường dùng cùng `hidePagination`. */
  scrollable?: boolean;
  hidePagination?: boolean;
  pageIndex?: number;
  pageSize?: number;
  totalItems?: number;
  label?: string;
  sortBy?: string;
  sortOrder?: SortDirection;
  onChangePage?: (page: number) => void;
  onChangePageSize?: (page: number) => void;
  onRequestSort?: (sortBy: string, sortOrder: SortDirection) => void;
  /** Thay thế chuỗi mặc định khi không có dữ liệu (vd. báo payload quá lớn). */
  emptyMessage?: string;
  /** Khi true, không render ô “không có dữ liệu” (dùng khi overlay đang loading). */
  suppressEmptyState?: boolean;
};

const DEFAULT_TABLE_SX = { minWidth: 'max-content' } as const;

const BaseTableReport = <T extends { id: string }>(props: BaseTableReportProps<T>) => {
  const { t } = useTranslation('admin/common');

  const {
    data,
    headCells,
    scrollable,
    hidePagination,
    totalItems,
    pageIndex,
    pageSize,
    label,
    sortBy,
    sortOrder,
    renderCell,
    onRequestSort,
    onChangePageSize,
    onChangePage,
    emptyMessage,
    suppressEmptyState,
  } = props;

  const handleChangePage = (_event: unknown, newPage: number) => {
    onChangePage?.(newPage);
  };

  const handleRequestSort = (nextSortBy: string, nextSortOrder: SortDirection) => {
    onRequestSort?.(nextSortBy, nextSortOrder);
  };

  const handleChangePageSize = (value: number) => {
    onChangePageSize?.(value);
  };

  const emptyColSpan = headCells.length > 0 ? headCells.length : 1;

  const tableBody = (
    <TableBody>
      {data.length === 0 ? (
        <TableRow>
          <TableCell
            colSpan={emptyColSpan}
            align='center'
            sx={{
              borderBottom: 'none',
              py: 6,
              verticalAlign: 'middle',
              minHeight: 'min(50vh, 400px)',
            }}
            role={suppressEmptyState ? undefined : 'status'}
            aria-hidden={suppressEmptyState ? true : undefined}
          >
            {!suppressEmptyState && (
              <Typography className='text-base text-[#404854] px-4 text-center'>
                {emptyMessage ?? t('noData', { ns: 'admin/common' })}
              </Typography>
            )}
          </TableCell>
        </TableRow>
      ) : (
        data.map((item: T, index: number) => {
          return (
            <TableRow
              hover
              tabIndex={-1}
              key={item.id}
              sx={
                scrollable
                  ? {
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                    }
                  : { cursor: 'pointer' }
              }
            >
              {renderCell(item, index)}
            </TableRow>
          );
        })
      )}
    </TableBody>
  );

  return (
    <Box className={cx('table-container')} sx={{ position: 'relative' }}>
      {scrollable ? (
        <ReportTableScrollContainer tableSx={DEFAULT_TABLE_SX}>
          <BaseTableHeadReport
            headCells={headCells}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRequestSort={handleRequestSort}
          />
          {tableBody}
        </ReportTableScrollContainer>
      ) : (
        <TableContainer className='rounded-t-xl border-b-0 bg-white'>
          <Table stickyHeader>
            <BaseTableHeadReport
              headCells={headCells}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onRequestSort={handleRequestSort}
            />
            {tableBody}
          </Table>
        </TableContainer>
      )}
      {!hidePagination && (
        <Box className={cx('pagination-container')}>
          <BasePagination
            label={label}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalItems={totalItems}
            count={Math.max(PAGE_INDEX, Math.ceil(Number(totalItems || 0) / (pageSize || PAGE_INDEX)))}
            onChange={handleChangePage}
            onChangePageSize={handleChangePageSize}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(BaseTableReport) as typeof BaseTableReport;
